import { useCallback, useEffect, useRef, useState } from 'react';

import { useLoading } from '@/hooks/use-loading.hook';
import { Appointment } from '@/types/appointments.types';
import { AppointmentsLifecycleEnum } from '@/types/enums/appointments.enum';
import { getAppointments } from '@/services/appointment/get-appointments.service';
import { getAppointmentById } from '@/services/appointment/get-by-appointment-id.service';

const APPOINTMENTS_PAGE_SIZE = 10;

interface UseFetchAppointmentsListParams {
  lifecycle: AppointmentsLifecycleEnum;
}

type AppointmentsFetchList = Array<Partial<Appointment> & { failed?: boolean }>;

export function useFetchAppointmentsList({ lifecycle }: UseFetchAppointmentsListParams) {
  const [list, setList] = useState<AppointmentsFetchList>([]);
  const [page, setPage] = useState<number>(1);
  const [listSize, setListSize] = useState<number>();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const hasFetchedFirstPage = useRef(false);

  useEffect(() => {
    async function fetchFirstAppointmentsPage() {
      startLoading();
      try {
        const res = await getAppointments({ lifecycle, page: 1 });
        if (res) {
          setList(res.appointmentsSummary);
          setListSize(res.appointmentsCount);
        }
      } finally {
        stopLoading();
      }
    }

    if (!hasFetchedFirstPage.current && lifecycle) {
      hasFetchedFirstPage.current = true;
      fetchFirstAppointmentsPage();
    }
  }, [lifecycle, startLoading, stopLoading]);

  useEffect(() => {
    async function fetchNextAppointmentsPage() {
      startLoading();
      try {
        const res = await getAppointments({ lifecycle, page });
        if (res) {
          setList((oldArray) => {
            const existingIds = new Set(oldArray.map((item) => item.id).filter((id): id is string => !!id));
            const newItems = res.appointmentsSummary.filter((item) => !!item.id && !existingIds.has(item.id));
            return [...oldArray, ...newItems];
          });
        }
      } finally {
        stopLoading();
      }
    }

    if (listSize && page > 1) {
      fetchNextAppointmentsPage();
    }
  }, [lifecycle, listSize, page, startLoading, stopLoading]);

  const fetchAppointmentById = useCallback(
    async (id?: string) => {
      const itemToUpdate = list?.find((item) => item.id === id);
      if (id && itemToUpdate && Object.keys(itemToUpdate).length < 3) {
        const res = await getAppointmentById(id);
        if (res) {
          setList((oldList) => oldList.map((item) => (item.id === id ? res : item)));
        } else {
          setList((oldList) => oldList.map((item) => (item.id === id ? { ...item, failed: true } : item)));
        }
      }
    },
    [list],
  );

  const onAppointmentUpdate = useCallback(
    (id: string, appointment: Partial<Appointment>) => {
      setList((oldList) =>
        oldList.map((item) => {
          if (item.id === id) {
            return { ...item, ...appointment };
          }
          return item;
        }),
      );
    },
    [setList],
  );

  const hideFailedTab = list.every((item) => item.failed) && Number(listSize) <= 10;

  return {
    list,
    listSize: hideFailedTab ? 0 : listSize,
    isLoading,
    fetchNextPage: () => setPage((oldPage) => oldPage + 1),
    hasFetchMore: list.length >= APPOINTMENTS_PAGE_SIZE && Number(listSize) > list.length,
    fetchAppointmentById,
    onAppointmentUpdate,
  };
}
